import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import SummaryApi from "../common";
import { FaCalendarAlt, FaPen , FaPlus } from "react-icons/fa";
import { FaPenNib } from "react-icons/fa6";
import NoImage from "../asset/noimage.png";

const BlogReport = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentBlog, setCurrentBlog] = useState(null);
  const [formData, setFormData] = useState({
    blog_id: "",
    title: "",
    subtitle: "",
    description1: "",
    description2: "",
    category: "",
    blog_image: null,
    blog_video: null,
    is_active: false,
  });

  const [imagePreview, setImagePreview] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const blogsPerPage = 6;

  // Theme detection - follow admin-theme from localStorage and listen for changes
  useEffect(() => {
    const updateTheme = () => {
      const adminTheme = sessionStorage.getItem('admin-theme');
      const shouldBeDark = adminTheme === 'dark';
      setIsDarkMode(shouldBeDark);
      document.documentElement.classList.toggle('dark', shouldBeDark);
    };

    // Initial theme setup
    updateTheme();

    // Listen for theme changes from AdminPanel
    const handleThemeChange = () => {
      updateTheme();
    };

    // Listen for storage changes
    const handleStorageChange = (e) => {
      if (e.key === 'admin-theme') {
        updateTheme();
      }
    };

    // Listen for custom admin theme events
    const handleAdminThemeToggled = () => {
      updateTheme();
    };

    window.addEventListener('themeChanged', handleThemeChange);
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('adminThemeToggled', handleAdminThemeToggled);

    return () => {
      window.removeEventListener('themeChanged', handleThemeChange);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('adminThemeToggled', handleAdminThemeToggled);
    };
  }, []);

  const fetchBlogs = async () => {
    try {
      const response = await fetch(SummaryApi.getAllBlog.url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const result = await response.json();
        if (result.status && Array.isArray(result.data)) {
          setBlogs(result.data);
        } else {
          console.error("Unexpected data format:", result);
        }
      } else {
        console.error("Failed to fetch blogs");
      }
    } catch (error) {
      console.error("Error fetching blogs:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  const handleEditClick = (blog) => {
    setCurrentBlog(blog);
    setFormData({
      blog_id: blog.blog_id,
      title: blog.title,
      subtitle: blog.subtitle,
      description1: blog.description1,
      description2: blog.description2 || "",
      category: blog.category,
      blog_image: null,
      blog_video: null,
      is_active: blog.is_active || false,
    });
    setImagePreview(null);
    setVideoPreview(null);
    setIsEditModalOpen(true);
  };

  const handleChange = (e) => {
    const { name, value, files, type, checked } = e.target;

    if (files) {
      const file = files[0];

      setFormData((prev) => ({
        ...prev,
        [name]: file,
      }));

      if (name === "blog_image") {
        setImagePreview(URL.createObjectURL(file));
      }

      if (name === "blog_video") {
        setVideoPreview(URL.createObjectURL(file));
      }
    } else if (type === "checkbox") {
      setFormData((prev) => ({
        ...prev,
        [name]: checked,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();

    try {
      const form = new FormData();
      form.append("blog_id", formData.blog_id);
      form.append("title", formData.title);
      form.append("subtitle", formData.subtitle);
      form.append("description1", formData.description1);
      form.append("description2", formData.description2);
      form.append("category", formData.category);
      form.append("is_active", formData.is_active ? "1" : "0");

      if (formData.blog_image) {
        form.append("blog_image", formData.blog_image);
      }

      if (formData.blog_video) {
        form.append("blog_video", formData.blog_video);
      }

      const response = await fetch(SummaryApi.updateBlog.url, {
        method: "POST",
        body: form,
      });

      if (response.ok) {
        const result = await response.json();
        if (result.status) {
          Swal.fire({
            icon: "success",
            title: "Updated!",
            text: "Blog updated successfully!",
            timer: 1500,
            showConfirmButton: false,
            background: isDarkMode ? '#1f2937' : '#fff',
            color: isDarkMode ? '#fff' : '#000',
          });
          setIsEditModalOpen(false);
          fetchBlogs();
        } else {
          Swal.fire({
            icon: "error",
            title: "Update Failed",
            text:
              result.message || "An error occurred while updating the blog.",
            background: isDarkMode ? '#1f2937' : '#fff',
            color: isDarkMode ? '#fff' : '#000',
          });
        }
      } else {
        Swal.fire({
          icon: "error",
          title: "Request Failed",
          text: "Failed to connect to the server.",
          background: isDarkMode ? '#1f2937' : '#fff',
          color: isDarkMode ? '#fff' : '#000',
        });
      }
    } catch (error) {
      console.error("Error updating blog:", error);
      Swal.fire({
        icon: "error",
        title: "Unexpected Error",
        text: "Something went wrong!",
        background: isDarkMode ? '#1f2937' : '#fff',
        color: isDarkMode ? '#fff' : '#000',
      });
    }
  };

  const getFullMediaUrl = (relativePath) => {
    if (!relativePath) return null;
    const normalizedPath = relativePath.replace(/\\/g, "/");
    const baseUrl = SummaryApi.Images.url.replace("/api", "");
    return `${baseUrl}/${normalizedPath}`;
  };

  const totalPages = Math.ceil(blogs.length / blogsPerPage);

  // Paginated slice
  const paginatedBlogs = blogs.slice(
    (currentPage - 1) * blogsPerPage,
    currentPage * blogsPerPage
  );
  
  const handlePrev = () => {
    if (currentPage > 1) setCurrentPage((prev) => prev - 1);
  };

  const handleNext = () => {
    if (currentPage < totalPages) setCurrentPage((prev) => prev + 1);
  };

  return (
    <div className={`p-6 min-h-screen transition-colors duration-300 ${
      isDarkMode ? 'bg-gray-900' : 'bg-gray-50'
    }`}>
      <div className="mb-6 flex items-center justify-center gap-2">
        <FaPenNib className={`text-xl mt-2 transition-colors duration-300 ${
          isDarkMode ? 'text-blue-400' : 'text-blue-600'
        }`} />
        <h2 className={`text-2xl font-bold transition-colors duration-300 ${
          isDarkMode ? 'text-blue-400' : 'text-blue-700'
        }`}>Blog Report</h2>
      </div>

      {loading ?  (
  <p className={`transition-colors duration-300 ${
    isDarkMode ? 'text-gray-400' : 'text-gray-500'
  }`}>Loading blogs...</p>
) : blogs.length === 0 ? (
  <div className="flex flex-col items-center justify-center py-12">
    <FaPenNib className={`text-5xl mb-4 transition-colors duration-300 ${
      isDarkMode ? 'text-gray-500' : 'text-gray-400'
    }`} />
    <p className={`text-lg mb-2 transition-colors duration-300 ${
      isDarkMode ? 'text-gray-400' : 'text-gray-600'
    }`}>No blogs found.</p>
    <p className={`text-sm mb-6 transition-colors duration-300 ${
      isDarkMode ? 'text-gray-500' : 'text-gray-500'
    }`}>Create your first blog to get started!</p>
    <button
      onClick={() => window.location.href = `${window.location.origin}/admin-panel/create-blog`}
      className={`px-6 py-3 rounded-md font-medium transition-all shadow-lg flex items-center gap-2 ${
        isDarkMode
          ? "bg-blue-600 hover:bg-blue-700 text-white"
          : "bg-blue-900 hover:bg-blue-800 text-white"
      }`}
    >
      <FaPlus />
      Add Blog
    </button>
  </div>
): blogs.length === 0 ? (
        <p className={`transition-colors duration-300 ${
          isDarkMode ? 'text-gray-400' : 'text-gray-500'
        }`}>No blogs found.</p>
      ) : (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {paginatedBlogs.map((blog) => (
            <div
              key={blog.blog_id}
              className={`rounded-lg shadow-md hover:shadow-lg transition-all duration-300 ${
                isDarkMode 
                  ? 'bg-gray-800 border border-gray-700 hover:border-blue-400' 
                  : 'bg-white hover:border-blue-300'
              }`}
            >
              <div className="relative">
                <img
                  src={
                    blog.blog_image ? getFullMediaUrl(blog.blog_image) : NoImage
                  }
                  alt={blog.title}
                  className="w-full h-48 object-cover rounded-t-lg"
                />

                {blog.category && (
                  <span className={`absolute top-2 left-2 text-[10px] px-2 py-1 rounded shadow transition-colors duration-300 ${
                    isDarkMode 
                      ? 'bg-blue-700 text-white' 
                      : 'bg-blue-600 text-white'
                  }`}>
                    {blog.category}
                  </span>
                )}

                <button
                  className={`absolute top-2 right-2 rounded-full p-1 shadow hover:bg-gray-100 transition-colors duration-300 ${
                    isDarkMode 
                      ? 'bg-gray-700 hover:bg-gray-600' 
                      : 'bg-white hover:bg-gray-100'
                  }`}
                  onClick={() => handleEditClick(blog)}
                  title="Edit Blog"
                >
                  <FaPen className={`text-sm transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`} />
                </button>
              </div>

              <div className="p-4">
                <h3 className={`text-lg font-semibold mb-2 min-h-[48px] transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-200' : 'text-gray-800'
                }`}>
                  {blog.title}
                </h3>

                <p className={`text-sm mb-2 min-h-[40px] transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  {blog.subtitle}
                </p>

                <p className={`text-sm line-clamp-3 mb-2 min-h-[60px] transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  {blog.description1}
                </p>

                {blog.blog_date && (
                  <div className="flex items-center text-[10px] mt-2 mb-2 min-h-[10px] transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-500' : 'text-gray-500'
                  }">
                    <FaCalendarAlt className="mr-1" />
                    {new Date(blog.blog_date).toLocaleDateString('en-US', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </div>
                )}

                <div
                  className={`mt-1 text-[10px] font-semibold transition-colors duration-300 ${
                    blog.is_active ? "text-green-500" : "text-red-500"
                  }`}
                >
                  {blog.is_active ? "Active" : "Inactive"}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
  {blogs.length > 0 && (
          <div className="flex justify-center items-center gap-4 mt-8">
            <button
              onClick={handlePrev}
              disabled={currentPage === 1}
              className={`px-4 py-3 rounded-full font-semibold transition-colors duration-300 ${
                currentPage === 1
                  ? isDarkMode 
                    ? "bg-gray-700 text-gray-500 cursor-not-allowed" 
                    : "bg-gray-200 text-gray-500 cursor-not-allowed"
                  : isDarkMode 
                    ? "bg-blue-700 hover:bg-blue-600 text-white" 
                    : "bg-blue-900 hover:bg-blue-800 text-white"
              }`}
            >
              Prev
            </button>

            <span className={`font-semibold transition-colors duration-300 ${
              isDarkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Page {currentPage} of {totalPages}
            </span>

            <button
              onClick={handleNext}
              disabled={currentPage === totalPages}
              className={`px-4 py-3 rounded-full font-semibold transition-colors duration-300 ${
                currentPage === totalPages
                  ? isDarkMode 
                    ? "bg-gray-700 text-gray-500 cursor-not-allowed" 
                    : "bg-gray-200 text-gray-500 cursor-not-allowed"
                  : isDarkMode 
                    ? "bg-blue-700 hover:bg-blue-600 text-white" 
                    : "bg-blue-900 hover:bg-blue-800 text-white"
              }`}
            >
              Next
            </button>
          </div>
        )}

      {/* Edit Modal */}
      {isEditModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className={`p-6 rounded-lg shadow-lg w-full max-w-lg relative overflow-auto max-h-[90vh] transition-colors duration-300 ${
            isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white'
          }`}>
            <h2 className={`text-xl font-semibold mb-4 transition-colors duration-300 ${
              isDarkMode ? 'text-white' : 'text-gray-800'
            }`}>Edit Blog</h2>

            <form
              onSubmit={handleUpdateSubmit}
              className="space-y-3"
              encType="multipart/form-data"
            >
              <div>
                <label className={`block font-medium mb-1 transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>Title</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="Title"
                  className={`w-full border px-3 py-3 rounded transition-colors duration-300 ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500' 
                      : 'border-gray-300 focus:border-blue-500 placeholder-gray-500'
                  }`}
                  required
                />
              </div>

              <div>
                <label className={`block font-medium mb-1 transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>Subtitle</label>
                <input
                  type="text"
                  name="subtitle"
                  value={formData.subtitle}
                  onChange={handleChange}
                  placeholder="Subtitle"
                  className={`w-full border px-3 py-3 rounded transition-colors duration-300 ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500' 
                      : 'border-gray-300 focus:border-blue-500 placeholder-gray-500'
                  }`}
                  required
                />
              </div>

              <div>
                <label className={`block font-medium mb-1 transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>Category</label>
                <input
                  type="text"
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  placeholder="Category"
                  className={`w-full border px-3 py-3 rounded transition-colors duration-300 ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500' 
                      : 'border-gray-300 focus:border-blue-500 placeholder-gray-500'
                  }`}
                />
              </div>

              <div className="flex justify-between gap-2">
                <div className="w-full">
                  <label className={`block font-medium mb-1 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>Description 1</label>
                  <textarea
                    name="description1"
                    value={formData.description1}
                    onChange={handleChange}
                    placeholder="Description 1"
                    className={`w-full border px-3 py-3 rounded transition-colors duration-300 ${
                      isDarkMode 
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500' 
                        : 'border-gray-300 focus:border-blue-500 placeholder-gray-500'
                    }`}
                    rows={3}
                  ></textarea>
                </div>

                <div className="w-full">
                  <label className={`block font-medium mb-1 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>Description 2</label>
                  <textarea
                    name="description2"
                    value={formData.description2}
                    onChange={handleChange}
                    placeholder="Description 2"
                    className={`w-full border px-3 py-3 rounded transition-colors duration-300 ${
                      isDarkMode 
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500' 
                        : 'border-gray-300 focus:border-blue-500 placeholder-gray-500'
                    }`}
                    rows={3}
                  ></textarea>
                </div>
              </div>

              <div className="flex gap-6 mt-4">
                {/* Image Upload */}
                <div className="flex-1">
                  <label className={`block mb-1 font-semibold transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>Upload Image:</label>
                  <input
                    type="file"
                    name="blog_image"
                    accept="image/*"
                    onChange={handleChange}
                    className={`w-full transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}
                  />
                  {imagePreview ? (
                    <img
                      src={imagePreview}
                      alt="Selected"
                      className="mt-2 w-32 h-20 object-cover rounded"
                    />
                  ) : (
                    currentBlog?.blog_image && (
                      <img
                        src={getFullMediaUrl(currentBlog.blog_image)}
                        alt="Current"
                        className="mt-2 w-32 h-20 object-cover rounded"
                      />
                    )
                  )}
                </div>

                {/* Video Upload */}
                <div className="flex-1">
                  <label className={`block mb-1 font-semibold transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>Upload Video:</label>
                  <input
                    type="file"
                    name="blog_video"
                    accept="video/*"
                    onChange={handleChange}
                    className={`w-full transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}
                  />
                  {videoPreview ? (
                    <video
                      src={videoPreview}
                      controls
                      className="mt-2 w-48 h-28 rounded"
                    />
                  ) : (
                    currentBlog?.blog_video && (
                      <video
                        src={getFullMediaUrl(currentBlog.blog_video)}
                        controls
                        className="mt-2 w-48 h-28 rounded"
                      />
                    )
                  )}
                </div>
              </div>

              {/* is_active toggle switch */}
              <div className="mt-4">
                <label className="inline-flex items-center cursor-pointer">
                  <div className="relative">
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleChange}
                      className="sr-only"
                    />
                    {/* Background track changes color */}
                    <div
                      className={`w-11 h-6 rounded-full shadow-inner transition-colors duration-300 ${
                        formData.is_active ? "bg-green-500" : "bg-red-500"
                      }`}
                    ></div>
                    {/* Slider knob */}
                    <div
                      className={`absolute top-1 w-4 h-4 rounded-full shadow transform transition-transform duration-300 ${
                        formData.is_active ? "left-[25px]" : "left-[5px]"
                      } ${
                        isDarkMode ? 'bg-gray-300' : 'bg-white'
                      }`}
                    ></div>
                  </div>
                  <span className={`ml-3 font-medium select-none transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Is Active
                  </span>
                </label>
              </div>

              <div className="flex justify-end gap-4 mt-6">
                <button
                  type="button"
                  onClick={() => setIsEditModalOpen(false)}
                  className={`px-4 py-3 rounded border transition-colors duration-300 ${
                    isDarkMode 
                      ? 'border-gray-600 text-gray-300 hover:bg-gray-700' 
                      : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`px-4 py-3 rounded text-white transition-colors duration-300 ${
                    isDarkMode 
                               ? 'bg-[#1C3664] hover:bg-blue-700 text-white'
              : 'bg-[#1C3664] text-white hover:bg-blue-800'
                  }`}
                >
                  Update
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogReport;